import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { getInvoiceHistory } from '../api';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';
import SegmentedControl from '../components/ui/SegmentedControl';
import { JcIcon } from '../components/jobcost/JobCostShared';
import { customerOfJob } from '../lib/statement';
import InvoiceExportMenu from '../components/ar/InvoiceExportMenu';
import { matchesSearch, noMatchText } from '../lib/pageSearch';
import {
  fmtDay, todayCentral, periodKey, stepPeriod, periodLabel, dateOf, SLOW_DAYS, payStatsOf, dayWord,
  money, moneyWhole, statusMeta, projectStatus, selectView, viewToParams,
} from '../lib/invoiceHistory';

/* ──────────────────────────────────────────────────────────────────
 * Invoice History (PSR #68, Duncan 2026-10-15) — a view of Finance → AR / Collections
 * (/ar?view=history; its own tab until the same day). Every CE invoice for every job,
 * from the AR mirror the AR pulls keep (server/routes/invoiceHistory.js has the definitions).
 * Laid out like Job Costs: a filter bar, a summary, then a card per job that opens to its
 * invoices. Filters: time period by invoice date (month / quarter / year / whole project),
 * status, PM, CM and sales person. (The CE-phase filter came out 2026-10-15 -- Duncan: "it's not
 * working like I imagined"; a job's phases are still in the data, and lib/invoiceHistory still
 * filters on one if asked.) Rows are divs, not a table: the old App.css forces every
 * table cell's colour in dark mode.
 * ────────────────────────────────────────────────────────────────── */

const PERIODS = [
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
  { value: 'all', label: 'Whole project' },
];
// By job = a card per job that opens to its invoices; List = one line per project for the period
// (Duncan 2026-10-15: "figure out invoicing per month ... one line item for each project").
const VIEWS = [
  { value: 'job', label: 'By job' },
  { value: 'list', label: 'By month' }, // Duncan 2026-10-15: "so the users can easier get to that view"
];
const storedView = () => { try { return localStorage.getItem('ih_view') === 'list' ? 'list' : 'job'; } catch { return 'job'; } };
const SORT_OPTIONS = [
  { value: 'open', label: 'Most open' },
  { value: 'past_due', label: 'Most past due' },
  { value: 'invoiced', label: 'Most invoiced' },
  { value: 'latest', label: 'Latest invoice' },
  { value: 'name', label: 'Job name' },
];
const SHOW_FIRST = 12; // invoices shown when a job opens; the rest behind "Show all"

const TONES = {
  success: { color: 'var(--success)', bg: 'var(--success-soft)' },
  // Open in blue, as Client View's In Progress: Summit's accent is olive, too close to the green of paid.
  blue: { color: 'var(--type-labor)', bg: 'color-mix(in oklch, var(--type-labor) 14%, transparent)' },
  danger: { color: 'var(--danger)', bg: 'var(--danger-soft)' },
  warning: { color: 'var(--warning)', bg: 'var(--warning-soft)' },
  neutral: { color: 'var(--text-3)', bg: 'var(--bg-surface-2)' },
};
const Pill = ({ tone, label, title }) => (
  <span title={title} style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: TONES[tone].color, background: TONES[tone].bg }}>
    {label}
  </span>
);
const Badge = ({ inv }) => <Pill {...statusMeta(inv)} />;

/* Job Costs' select, so the filter bars look alike (as on BoMoCo). */
const Select = ({ value, onChange, options, label }) => (
  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className="ih-select"
      style={{ appearance: 'none', WebkitAppearance: 'none', height: 32, padding: '0 32px 0 12px', borderRadius: 8, minWidth: 150, fontSize: 14, fontWeight: 500, color: value ? 'var(--accent-text)' : 'var(--text-1)', fontFamily: 'var(--font-sans)', background: 'var(--bg-surface)', border: `1px solid ${value ? 'color-mix(in oklch, var(--accent) 45%, var(--border-default))' : 'var(--border-default)'}`, boxShadow: 'var(--shadow-xs)', cursor: 'pointer', letterSpacing: '-0.005em' }}
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <span style={{ position: 'absolute', right: 10, color: 'var(--text-3)', pointerEvents: 'none', display: 'inline-flex' }}><JcIcon name="chevron-d" size={14} /></span>
  </div>
);

const iconBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-2)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer' };
const card = { background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' };
const LABEL = { fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-3)' };
const JOB_GRID = 'minmax(0, 1.7fr) minmax(0, 1.1fr) repeat(3, minmax(108px, 0.8fr)) minmax(128px, 0.8fr) 66px';
const INV_GRID = '96px 112px 96px repeat(3, minmax(96px, 1fr)) 150px';

const Stat = ({ label, value, sub, color, title }) => (
  <div title={title} style={{ minWidth: 0 }}>
    <div style={LABEL}>{label}</div>
    <div className="mono" style={{ fontSize: 20, fontWeight: 700, color: color || 'var(--text-1)', letterSpacing: '-0.02em', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {sub && <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
  </div>
);

const Bar = ({ collected, open, pastDue, total }) => {
  const pct = (v) => (total > 0 ? Math.max(0, Math.min(100, (v / total) * 100)) : 0);
  return (
    <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-surface-2)', overflow: 'hidden', display: 'flex' }}>
      <span style={{ width: `${pct(collected)}%`, background: 'var(--success)' }} />
      <span style={{ width: `${pct(open - pastDue)}%`, background: 'var(--type-labor)' }} />
      <span style={{ width: `${pct(pastDue)}%`, background: 'var(--danger)' }} />
    </div>
  );
};

const NumCell = ({ label, value, color, sub, title }) => (
  <div title={title} style={{ textAlign: 'right', minWidth: 0 }}>
    <div style={LABEL}>{label}</div>
    <div className="mono" style={{ fontSize: 14, fontWeight: 600, color: color || 'var(--text-1)', marginTop: 3, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</div>
    {/* Always drawn (blank when there's no sub), as on Job Costs: every column is the same
        height, so the labels stay on one line across the card. */}
    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub || '\u00a0'}</div>
  </div>
);

/* ── List view: one line per project for the period, with a totals line ── */
const LIST_GRID = 'minmax(220px, 2.2fr) 110px repeat(3, minmax(112px, 1fr)) 132px';

const ListView = ({ jobs, totals, onOpenProject }) => {
  const head = { display: 'grid', gridTemplateColumns: LIST_GRID, gap: 12, padding: '9px 18px', ...LABEL };
  const cellMoney = (v, color) => (
    <span className="mono" style={{ textAlign: 'right', color: Math.abs(v) > 0.004 ? (color || 'var(--text-1)') : 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      {Math.abs(v) > 0.004 ? money(v) : '–'}
    </span>
  );
  return (
    <section className="ih-clip" style={card}>
      <div className="ih-hscroll">
        <div style={{ minWidth: 900 }}>
          <div className="ih-listhead" style={{ ...head, background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-subtle)' }}>
            <span>Project</span><span>Invoice</span>
            <span style={{ textAlign: 'right' }}>Invoiced</span><span style={{ textAlign: 'right' }}>Collected</span><span style={{ textAlign: 'right' }}>Open</span>
            <span>Status</span>
          </div>
          {jobs.map((j) => {
            const dated = [...j.invoices].sort((a, b) => String(dateOf(a) || '').localeCompare(String(dateOf(b) || '')));
            const few = dated.length <= 2;
            const credits = dated.filter((i) => i.amount < 0).length; // credit memos aren't counted as invoices
            return (
              <div key={j.job} className="ih-inv" style={{ display: 'grid', gridTemplateColumns: LIST_GRID, gap: 12, alignItems: 'start', padding: '10px 18px', borderTop: '1px solid var(--border-subtle)', fontSize: 13.5 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.name}>{j.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2, display: 'flex', gap: 6, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    {j.slug && onOpenProject ? (
                      <button type="button" className="ih-link mono" title="Open the project" onClick={() => onOpenProject(j.slug)}>#{j.job} <JcIcon name="external" size={10} /></button>
                    ) : <span className="mono">#{j.job}</span>}
                    {j.pm.length > 0 && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>· PM {j.pm.join(', ')}</span>}
                  </div>
                </div>
                <div className="mono" style={{ color: 'var(--text-1)', lineHeight: 1.5 }}>
                  {few ? dated.map((i) => <div key={i.number}>{i.number}</div>) : (
                    <span style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-2)' }}>
                      {j.t.count} invoice{j.t.count === 1 ? '' : 's'}
                      {credits > 0 && <span style={{ color: 'var(--text-3)' }}> + {credits} credit{credits === 1 ? '' : 's'}</span>}
                    </span>
                  )}
                </div>
                {cellMoney(j.t.invoiced)}
                {cellMoney(j.t.collected, 'var(--text-2)')}
                {cellMoney(j.t.open, j.t.pastDue > 0 ? 'var(--danger)' : undefined)}
                <span><Pill {...projectStatus(j)} /></span>
              </div>
            );
          })}
          <div style={{ display: 'grid', gridTemplateColumns: LIST_GRID, gap: 12, padding: '11px 18px', borderTop: '2px solid var(--border-strong)', fontSize: 13.5, fontWeight: 700, background: 'var(--bg-surface-2)' }}>
            <span style={{ color: 'var(--text-1)' }}>{jobs.length} project{jobs.length === 1 ? '' : 's'} · {totals.count} invoice{totals.count === 1 ? '' : 's'}</span>
            <span />
            {cellMoney(totals.invoiced)}
            {cellMoney(totals.collected)}
            {cellMoney(totals.open, totals.pastDue > 0 ? 'var(--danger)' : undefined)}
            <span />
          </div>
        </div>
      </div>
    </section>
  );
};

const InvoiceHistoryPage = ({ onOpenProject, search }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // By month opens on Month; By job on the whole project.
  const [periodType, setPeriodType] = useState(() => (storedView() === 'list' ? 'month' : 'all'));
  const [pickedAt, setPickedAt] = useState(null); // the period you stepped to; none = the latest with invoices
  const [status, setStatus] = useState('all');
  const [pm, setPm] = useState('');
  const [cm, setCm] = useState('');
  const [sales, setSales] = useState('');
  const [sortBy, setSortBy] = useState('open');
  const [view, setView] = useState(storedView);
  const [openJobs, setOpenJobs] = useState(() => new Set());
  const [showAll, setShowAll] = useState(() => new Set());

  const load = useCallback(() => {
    setError(null);
    getInvoiceHistory()
      .then((d) => setData(d))
      .catch((e) => setError(e.message || 'Could not load the invoice history'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const jobs = useMemo(() => (data && data.jobs) || [], [data]);

  // The period stepper runs from the first invoice's period to the current one.
  const bounds = useMemo(() => {
    const dates = [];
    jobs.forEach((j) => j.invoices.forEach((i) => { const d = dateOf(i); if (d) dates.push(d); }));
    dates.sort();
    const today = todayCentral();
    const latest = dates.filter((d) => d <= today).pop() || today;
    return { first: dates[0] || today, last: today, latest };
  }, [jobs]);
  // Opens on the latest period that has invoices (early in a quarter the current one is empty);
  // › still steps on to the current period.
  const choosePeriodType = (t) => {
    setPeriodType(t);
    setPickedAt(t === 'all' ? null : periodKey(bounds.latest, t));
  };
  const periodAt = periodType === 'all' ? null : (pickedAt || periodKey(bounds.latest, periodType));
  // By month is one click to the monthly view: it also switches the period to Month.
  const chooseView = (v) => {
    setView(v);
    try { localStorage.setItem('ih_view', v); } catch { /* private window: just don't remember */ }
    if (v === 'list') choosePeriodType('month');
  };
  const firstKey = periodType === 'all' ? null : periodKey(bounds.first, periodType);
  const lastKey = periodType === 'all' ? null : periodKey(bounds.last, periodType);

  // The top bar's search (ARPage holds it for this view; lib/pageSearch): an invoice shows when every
  // word is in its number or status or its job's number, name or people. It narrows the jobs before
  // selectView, so the status counts and the summary follow it as they follow the PM / CM / sales
  // filters. The exports don't take it: their link carries the filters, not the search.
  const searched = useMemo(() => jobs.map((j) => ({
    ...j,
    invoices: j.invoices.filter((i) => matchesSearch(search, j.job, j.name, j.pm, j.cm, j.sales, i.number, statusMeta(i).label)),
  })), [jobs, search]);

  // The jobs showing and their figures (lib/invoiceHistory; the exports use the same).
  const { counts, visible, totals, pay } = useMemo(
    () => selectView(searched, { periodType, periodAt, status, pm, cm, sales, sortBy }),
    [searched, periodType, periodAt, status, pm, cm, sales, sortBy],
  );

  // Drop-down choices come from every job, so a choice never vanishes when the period changes.
  const options = useMemo(() => {
    const uniq = (field) => [...new Set(jobs.flatMap((j) => j[field]))].sort((a, b) => a.localeCompare(b));
    return {
      pm: [{ value: '', label: 'All PMs' }, ...uniq('pm').map((n) => ({ value: n, label: n }))],
      cm: [{ value: '', label: 'All CMs' }, ...uniq('cm').map((n) => ({ value: n, label: n }))],
      sales: [{ value: '', label: 'All sales people' }, ...uniq('sales').map((n) => ({ value: n, label: n }))],
    };
  }, [jobs]);

  const toggle = (setter, key) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // By month's column labels stick under the frozen filter bar, whose height depends on how it wraps.
  const rootRef = useRef(null);
  const barRef = useRef(null);
  useLayoutEffect(() => {
    const root = rootRef.current;
    const bar = barRef.current;
    if (!root || !bar) return undefined;
    const set = () => root.style.setProperty('--ih-filter-h', `${bar.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [data]);

  if (error) return <StateScreen kind="error" title="Couldn't load the invoice history" body={error} primary={{ label: 'Try again', icon: 'refresh-cw', onClick: load }} />;
  if (!data) return <LoadingState message="Loading invoice history…" />;
  if (jobs.length === 0) return <StateScreen kind="empty" title="No invoices yet" body="Invoice history comes from the AR pulls (Settings → AR Sync). None have landed yet." />;

  const periodName = periodType === 'all' ? 'to date' : `· ${periodLabel(periodAt, periodType)}`;
  // Exports: the report page and the workbook get the page's choices (and a job, from its card).
  const viewNow = (job) => ({ periodType, periodAt, status, pm, cm, sales, sortBy, job: job || '' });
  const reportPath = (job) => `/ar/invoice-report?${viewToParams(viewNow(job)).toString()}`;
  const downloadExcel = async (job) => {
    const { downloadInvoiceWorkbook } = await import('../lib/invoiceXlsx'); // ExcelJS only when asked
    await downloadInvoiceWorkbook(data, viewNow(job));
  };
  const anyPeople = pm || cm || sales;

  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <style>{`
        /* Frozen like the rest of AR / Collections (ARPage.js): the filter bar, and By month's column
           labels under it, stay in view while the page scrolls -- where the list fits across. */
        .ih-clip { overflow: hidden; overflow: clip; }
        .ih-hscroll { overflow-x: auto; }
        @media (min-width: 1290px) {
          .ih-hscroll { overflow-x: clip; }
          .ih-sticky { position: sticky; top: 56px; z-index: 6; }
          .ih-listhead { position: sticky; top: calc(56px + var(--ih-filter-h, 0px)); z-index: 3; }
        }
        .ih-select:focus-visible, .ih-step:focus-visible { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
        .ih-step:disabled { opacity: .4; cursor: default; }
        .ih-job:hover { border-color: var(--border-strong); }
        .ih-link { background: none; border: none; padding: 0; font: inherit; color: inherit; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; }
        .ih-link:hover { color: var(--accent-text); }
        .ih-inv:hover { background: var(--bg-hover); }
      `}</style>

      {/* Filter bar: when, then which jobs. */}
      <section className="ih-sticky" ref={barRef} style={{ ...card, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <SegmentedControl value={view} onChange={chooseView} options={VIEWS} />
          <SegmentedControl value={periodType} onChange={choosePeriodType} options={PERIODS} />
          {periodType !== 'all' && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <button type="button" className="ih-step" style={iconBtn} aria-label="Previous period" disabled={periodAt <= firstKey} onClick={() => setPickedAt(stepPeriod(periodAt, periodType, -1))}>‹</button>
              <span style={{ minWidth: 92, textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>{periodLabel(periodAt, periodType)}</span>
              <button type="button" className="ih-step" style={iconBtn} aria-label="Next period" disabled={periodAt >= lastKey} onClick={() => setPickedAt(stepPeriod(periodAt, periodType, 1))}>›</button>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 8 }} />
          <SegmentedControl
            value={status}
            onChange={setStatus}
            options={[
              { value: 'all', label: 'All', count: counts.all },
              { value: 'open', label: 'Open', count: counts.open },
              { value: 'past_due', label: 'Past due', count: counts.past_due },
              { value: 'paid', label: 'Paid', count: counts.paid },
            ]}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Select label="PM" value={pm} onChange={setPm} options={options.pm} />
          <Select label="CM" value={cm} onChange={setCm} options={options.cm} />
          <Select label="Sales person" value={sales} onChange={setSales} options={options.sales} />
          {anyPeople && (
            <button type="button" onClick={() => { setPm(''); setCm(''); setSales(''); }} style={{ height: 32, padding: '0 11px', borderRadius: 8, fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer' }}>
              Clear
            </button>
          )}
          <div style={{ flex: 1, minWidth: 8 }} />
          <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)' }}>Sort by</span>
          <Select label="Sort by" value={sortBy} onChange={setSortBy} options={SORT_OPTIONS} />
          <button type="button" onClick={load} style={iconBtn} title="Refresh" aria-label="Refresh invoice history"><JcIcon name="refresh" size={15} /></button>
          {/* PDF report / Excel of what's showing (Duncan 2026-10-15: "the same reporting and exporting that job cost has"). */}
          <InvoiceExportMenu heading="Export what's showing" reportPath={reportPath()} onExcel={() => downloadExcel()} />
        </div>
      </section>

      {/* Summary of what's showing. */}
      <section style={{ ...card, padding: '14px 18px', display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 16 }}>
        <Stat label={`Invoiced ${periodName}`} value={moneyWhole(totals.invoiced)} sub={`${totals.count} invoice${totals.count === 1 ? '' : 's'} · ${visible.length} job${visible.length === 1 ? '' : 's'}`} />
        <Stat label="Collected" value={moneyWhole(totals.collected)} color="var(--success)" sub={totals.invoiced > 0 ? `${((totals.collected / totals.invoiced) * 100).toFixed(1)}% of it` : null} />
        <Stat label="Open" value={moneyWhole(totals.open)} sub={`${totals.openCount} invoice${totals.openCount === 1 ? '' : 's'}`} title={data.as_of ? `Open balances as of ${fmtDay(data.as_of)} (the latest AR pull)` : undefined} />
        <Stat label="Past due" value={moneyWhole(totals.pastDue)} color={totals.pastDue > 0 ? 'var(--danger)' : undefined} sub={`${totals.pastDueCount} invoice${totals.pastDueCount === 1 ? '' : 's'}`} />
        <Stat
          label="Avg days to pay"
          value={pay.all ? dayWord(pay.all.avg) : '–'}
          sub={pay.all ? `median ${pay.all.median} · ${pay.all.n} paid` : 'none paid yet'}
          title="From the invoice date to the final payment, for the invoices showing that are paid in full"
        />
      </section>

      {visible.length === 0 ? (
        <StateScreen kind="empty" title="No invoices match"
          body={search && search.trim() ? noMatchText(search).body : 'Try another period or filter, or switch back to All.'} />
      ) : view === 'list' ? (
        <ListView jobs={visible} totals={totals} onOpenProject={onOpenProject} />
      ) : visible.map((j) => {
        const isOpen = openJobs.has(j.job);
        const people = [j.pm.length ? `PM ${j.pm.join(', ')}` : null, j.cm.length ? `CM ${j.cm.join(', ')}` : null, j.sales.length ? `Sales ${j.sales.join(', ')}` : null].filter(Boolean).join(' · ');
        const pct = j.t.invoiced > 0 ? Math.round((j.t.collected / j.t.invoiced) * 100) : null;
        const shown = isOpen ? (showAll.has(j.job) ? j.invoices : j.invoices.slice(0, SHOW_FIRST)) : [];
        const own = payStatsOf(j.pay);
        const code = customerOfJob(j);
        const cust = pay.customers.get(code);
        const payTitle = [
          own ? `This job: ${dayWord(own.avg)} on average over ${own.n} paid invoice${own.n === 1 ? '' : 's'}.` : 'This job: no paid invoices here yet.',
          cust ? `Customer ${code}: ${dayWord(cust.avg)} over ${cust.n} paid invoice${cust.n === 1 ? '' : 's'} on ${cust.jobs} job${cust.jobs === 1 ? '' : 's'}.` : null,
          `From the invoice date to the final payment. Red: over ${SLOW_DAYS} days.`,
        ].filter(Boolean).join('\n');
        return (
          <section key={j.job} className="ih-job" style={{ ...card, overflow: 'hidden' }}>
            <div
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              onClick={() => toggle(setOpenJobs, j.job)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(setOpenJobs, j.job); } }}
              style={{ display: 'grid', gridTemplateColumns: JOB_GRID, gap: 16, alignItems: 'center', padding: '14px 18px', cursor: 'pointer' }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.name}>{j.name}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2, display: 'flex', gap: 6, alignItems: 'center', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  {j.slug && onOpenProject ? (
                    <button type="button" className="ih-link mono" title="Open the project" onClick={(e) => { e.stopPropagation(); onOpenProject(j.slug); }}>
                      #{j.job} <JcIcon name="external" size={11} />
                    </button>
                  ) : <span className="mono">#{j.job}</span>}
                  <span>· {j.t.count} invoice{j.t.count === 1 ? '' : 's'}{periodType === 'all' ? '' : ` in ${periodLabel(periodAt, periodType)}`}</span>
                </div>
                {people && <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={people}>{people}</div>}
              </div>
              <div style={{ minWidth: 0 }}>
                <Bar collected={j.t.collected} open={j.t.open} pastDue={j.t.pastDue} total={j.t.invoiced} />
                {/* The full line on hover: on a narrow screen its end ("· last Sep 30") is cut off. */}
                <div title={[j.bucket === 'paid' ? 'Paid in full' : `${pct == null ? '–' : `${pct}%`} collected`, j.t.pastDueCount > 0 ? `${j.t.pastDueCount} past due` : null, j.t.latest ? `last ${fmtDay(j.t.latest, false)}` : null].filter(Boolean).join(' · ')} style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {j.bucket === 'paid' ? 'Paid in full' : `${pct == null ? '–' : `${pct}%`} collected`}
                  {j.t.pastDueCount > 0 && <span style={{ color: 'var(--danger)' }}> · {j.t.pastDueCount} past due</span>}
                  {j.t.latest && <span> · last {fmtDay(j.t.latest, false)}</span>}
                </div>
              </div>
              <NumCell label="Invoiced" value={money(j.t.invoiced)} />
              <NumCell label="Collected" value={money(j.t.collected)} />
              <NumCell label="Open" value={j.t.open > 0.004 ? money(j.t.open) : '–'} color={j.t.pastDue > 0 ? 'var(--danger)' : undefined} />
              <NumCell
                label="Days to pay"
                value={own ? dayWord(own.avg) : '–'}
                color={own && own.avg > SLOW_DAYS ? 'var(--danger)' : undefined}
                sub={cust ? `customer ${cust.avg} · ${cust.jobs} job${cust.jobs === 1 ? '' : 's'}` : null}
                title={payTitle}
              />
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                <InvoiceExportMenu variant="icon" heading="Export this job" reportPath={reportPath(j.job)} onExcel={() => downloadExcel(j.job)} />
                <span style={{ color: 'var(--text-3)', display: 'inline-flex', justifyContent: 'center', width: 28, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}><JcIcon name="chevron-d" size={16} /></span>
              </span>
            </div>
            {isOpen && (
              <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '6px 18px 12px', background: 'var(--bg-surface-2)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: INV_GRID, gap: 12, padding: '6px 8px', ...LABEL }}>
                  <span>Invoice</span><span>Date</span><span>Due</span>
                  <span style={{ textAlign: 'right' }}>Amount</span><span style={{ textAlign: 'right' }}>Collected</span><span style={{ textAlign: 'right' }}>Open</span>
                  <span>Status</span>
                </div>
                {shown.map((i) => {
                  const sameYear = i.due && i.date && i.due.slice(0, 4) === i.date.slice(0, 4);
                  return (
                    <div key={i.number} className="ih-inv" style={{ display: 'grid', gridTemplateColumns: INV_GRID, gap: 12, alignItems: 'center', padding: '7px 8px', borderTop: '1px solid var(--border-subtle)', fontSize: 13.5 }}>
                      <span className="mono" style={{ color: 'var(--text-1)' }}>{i.number}</span>
                      <span style={{ color: 'var(--text-2)' }}>{fmtDay(dateOf(i))}</span>
                      <span style={{ color: 'var(--text-3)' }}>{i.due ? fmtDay(i.due, !sameYear) : '–'}</span>
                      <span className="mono" style={{ textAlign: 'right', color: i.amount < 0 ? 'var(--text-3)' : 'var(--text-1)' }}>{money(i.amount)}</span>
                      <span className="mono" style={{ textAlign: 'right', color: 'var(--text-2)' }}>{Math.abs(i.collected) > 0.004 ? money(i.collected) : '–'}</span>
                      <span className="mono" style={{ textAlign: 'right', color: i.open > 0.004 ? 'var(--text-1)' : 'var(--text-3)' }}>{Math.abs(i.open) > 0.004 ? money(i.open) : '–'}</span>
                      <span><Badge inv={i} /></span>
                    </div>
                  );
                })}
                {j.invoices.length > SHOW_FIRST && !showAll.has(j.job) && (
                  <button type="button" onClick={() => toggle(setShowAll, j.job)} style={{ marginTop: 8, background: 'none', border: 'none', padding: '4px 8px', color: 'var(--accent-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Show all {j.invoices.length} invoices
                  </button>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default InvoiceHistoryPage;
